import { SyntheticEvent, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { captureMessage } from '@sentry/nextjs'

import { InputV2 } from '~/components/InputV2'
import registerAnalytics from '~/analytics/register/registerAnalytics'
import useRegisterContext from '~/auth/hooks/useRegisterContext'
import useReCaptchaV3 from '~/auth/hooks/useReCaptchaV3'
import CheckBox from '~/checklist/components/CheckBox'
import { ButtonPrimary } from '~/components/Buttons'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import EyeOpenIcon from '~/components/Icons/svgs/EyeOpenIcon'
import EyeClosedIcon from '~/components/Icons/svgs/EyeClosedIcon'
import { useRegisterCampaignUserMutation } from '~/auth/apis/auth.api'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { loginUser } from '~/auth/slices/auth.slice'
import { selectCaptcha, setCaptcha, resetCaptcha } from '~/auth/slices/recaptcha.slice'
import CaptchaChallenge from '~/auth/components/CaptchaChallenge'
import { LoginType } from '~/analytics/account/accountEvents.types'
import { setIsPreSignupActionCreator } from '~/onboarding/redux/onboarding.slice'
import { AuthenticationResponse, SuccessResult } from '~/redux/apis/types'
import InfoIIcon from '~/components/Icons/svgs/InfoIIcon'
import ROUTES from '~/shared/constants/routes.const'

export default function MakeAWishRegistrationForm(): React.ReactElement {
  const { t } = useTranslation(['makeAWish', 'fields', 'errors'])
  const { fields, setFields } = useRegisterContext()
  const dispatch = useAppDispatch()
  const router = useRouter()
  const { getReCaptchaToken } = useReCaptchaV3()
  const captcha = useAppSelector(selectCaptcha)

  const [registerCampaignUser, { data: response, isLoading, isSuccess, error }] = useRegisterCampaignUserMutation()

  const [eligibilityAccepted, setEligibilityAccepted] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [serverError, setServerError] = useState('')
  const [loading, setLoading] = useState(false)

  const isFormComplete = Boolean(fields.email?.trim()) && Boolean(fields.password?.trim()) && eligibilityAccepted

  useEffect(() => {
    if (isSuccess && response?.type === 'success') {
      const successResponse = response as SuccessResult<AuthenticationResponse>
      dispatch(loginUser({ loginType: LoginType.EMAIL, result: successResponse }))
      dispatch(setIsPreSignupActionCreator(false))
      dispatch(resetCaptcha())
      router.push(ROUTES.CAMPAIGNS_MAKEAWISH_SUCCESS)
    }

    if (error) {
      setLoading(false)
      if ('data' in error) {
        const { data } = error as { data: { type: string; message: string; details?: { captchaChallenge?: string } } }
        if (data?.details?.captchaChallenge) {
          setServerError('')
          dispatch(setCaptcha({ answer: '', challenge: data.details.captchaChallenge }))
        } else {
          setServerError(data?.message || t('errors:unknown'))
        }
      } else {
        setServerError(t('errors:unknown'))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, response, error])

  const submitRegistration = useCallback(async () => {
    if (loading) return
    setLoading(true)
    setServerError('')

    const recaptchaToken = (await getReCaptchaToken('signup/campaign')) ?? ''
    if (!recaptchaToken) {
      captureMessage('Sign-up stopped: no reCAPTCHA token', {
        level: 'warning',
        tags: { feature: 'signup', campaign: 'make-a-wish' },
      })
      setLoading(false)
      setServerError(t('errors:recaptchaUnavailable'))
      return
    }

    await registerCampaignUser({
      email: fields.email!,
      password: fields.password!,
      campaign: 'make-a-wish',
      recaptchaToken,
      captchaAnswer: captcha.answer || undefined,
    })

    setLoading(false)
  }, [loading, getReCaptchaToken, registerCampaignUser, fields.email, fields.password, captcha.answer, t])

  const handleOnChangeTextFields = (name: string, value: string): void => {
    if (value.length === 1) {
      registerAnalytics.writingInForms(name)
    }
    setFields((prev) => ({
      ...prev,
      [name]: value,
    }))
    if (serverError) {
      setServerError('')
    }
  }

  const handleFormSubmit = (e: SyntheticEvent): void => {
    e.preventDefault()
    registerAnalytics.logButtonClick('register')
    submitRegistration()
  }

  return (
    <div className="flex flex-col w-full max-w-132 pointer-events-auto h-full justify-center items-center md:items-start">
      <h2 className="mb-3 text-3xl font-bold text-white md:text-10 md:leading-12 text-center md:text-start">
        <span className="block md:inline">{t('makeAWish:freeFor')}</span>{' '}
        <span className="block md:inline">{t('makeAWish:makeAWish')}</span>{' '}
        <span className="block md:inline">{t('makeAWish:families')}</span>
      </h2>
      <div className="flex flex-col justify-center items-center w-87.5 h-full md:h-fit md:w-117 py-3.5 px-3 md:px-6 rounded-20 border border-blue-pastel backdrop-blur-xl md:backdrop-blur-none">
        <h2 className="mb-3 text-lg font-bold text-white md:text-xl">{t('makeAWish:formTitle')}</h2>
        <form className="flex flex-col gap-1" onSubmit={handleFormSubmit}>
          <InputV2
            autoComplete="email"
            className="!bg-purple-darker/30"
            disabled={isLoading}
            inputClassName="!text-white placeholder:text-white/60 !border-purple/30"
            label={t('fields:email')}
            labelClassName="!text-blue-pastel"
            name="email"
            setValue={(val) => handleOnChangeTextFields('email', val)}
            type="email"
            value={fields.email || ''}
          />
          <div className="relative">
            <InputV2
              autoComplete="new-password"
              className="!bg-purple-darker/30"
              disabled={isLoading}
              inputClassName="!text-white placeholder:text-white/60 !border-purple/30"
              label={t('fields:createPassword')}
              labelClassName="!text-blue-pastel"
              name="password"
              setValue={(val) => handleOnChangeTextFields('password', val)}
              type={showPassword ? 'text' : 'password'}
              value={fields.password || ''}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer w-7 h-4 flex items-center justify-center">
              <SVGIcon
                className={`absolute duration-500 ${showPassword ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                icon={EyeClosedIcon}
                onClick={() => setShowPassword(false)}
                size={27}
              />
              <SVGIcon
                className={`absolute duration-500 ${showPassword ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                icon={EyeOpenIcon}
                onClick={() => setShowPassword(true)}
                size={30}
              />
            </div>
          </div>

          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            className={`flex items-center gap-2 text-left text-xs text-blue-pastel ${
              fields.email?.trim() && fields.password?.trim()
                ? 'cursor-pointer opacity-100'
                : '!cursor-not-allowed opacity-80 [&_button]:!cursor-not-allowed [&_span]:!cursor-not-allowed'
            }`}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (!fields.email?.trim() || !fields.password?.trim()) return
              setEligibilityAccepted((prev) => !prev)
            }}
          >
            <CheckBox isCompleted={eligibilityAccepted} onCheckboxClick={() => {}} />
            <span>{t('makeAWish:eligibilityLabel')}</span>
          </div>
          {captcha.challenge ? <CaptchaChallenge hideSubmitBtn loading={loading || isLoading} /> : null}
          <ButtonPrimary
            className="bg-gradient-purple-violet !font-black !text-white text-md drop-shadow-black shadow-black h-13 rounded-20 border-2 border-white mt-2"
            disabled={loading || isLoading || !isFormComplete}
            disableRing
            isFullWidth
            isLoading={loading || isLoading}
            noPadding
            size="medium"
            type="submit"
          >
            {t('makeAWish:activateAccess')}
          </ButtonPrimary>
          {serverError ? (
            <div className="flex items-start gap-2  bg-orange-pastel p-2 rounded my-1">
              <div className="w-7 bg-red-error rounded p-1">
                <SVGIcon icon={InfoIIcon} pathClassName="text-white" size={15} />
              </div>
              <p className="text-black leading-5">{t('makeAWish:registrationFailed')}</p>
            </div>
          ) : null}
        </form>
      </div>
    </div>
  )
}
